import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import Topbar from "./scenes/global/Topbar";
import Sidebar from "./scenes/global/Sidebar";
import Dashboard from "./scenes/dashboard";
import Team from "./scenes/team";
import Brevets from "./scenes/brevets";
import Contrat from "./scenes/contrat";
import Alerte from "./scenes/Alerte";
import Contacts from "./scenes/contacts";
import NomDomaine from "./scenes/NomDomaine";
import Contact from "./scenes/Contact";
import Bar from "./scenes/bar";
import Form from "./scenes/form";
import Line from "./scenes/line";
import Pie from "./scenes/pie";
import Geography from "./scenes/geography";
import Calendrier from "./scenes/calendrier/calendrier";
import Login from "./components/login/Login";
import ResetPassword from "./components/login/ResetPassword";
import ForgotPassword from "./components/login/ForgotPassword";
import UpdatePassword from "./components/login/UpdatePassword";
import Register from "./components/login/Register";
import EditProfil from "./scenes/Profil/EditProfil";
import { CssBaseline, ThemeProvider, Box } from "@mui/material";
import { ColorModeContext, useMode } from "./theme";
import AuthService from "./api/services/AuthService";
import { Snackbar, Alert, Slide } from "@mui/material";

function App() {
  const [theme, colorMode] = useMode();
  const [isSidebar, setIsSidebar] = useState(true);
  const [isCollapsed, setIsCollapsed] = useState(window.innerWidth < 900);
  const [isLoggedIn, setIsLoggedIn] = useState(
    localStorage.getItem("isLoggedIn") === "true"
  );
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isSignUp, setIsSignUp] = useState(false);
  const navigate = useNavigate();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  useEffect(() => {
    const checkAuth = async () => {
      const logged = localStorage.getItem("isLoggedIn") === "true";
      setIsLoggedIn(logged);
      setIsCheckingAuth(false);
    };
    checkAuth();
  }, []);

  const handleLoginSuccess = (token, userId, userRole, userData) => {
    localStorage.setItem("isLoggedIn", "true");
    localStorage.setItem("token", token);
    localStorage.setItem("user_id", userId);
    localStorage.setItem("user_role", userRole);
    localStorage.setItem("user_data", JSON.stringify(userData));
    setSnackbarSeverity("success");
    setSnackbarMessage(`Bienvenue, vous êtes connecté(e) en tant que ${userRole}`);
    setSnackbarOpen(true);
    setIsLoggedIn(true);
    navigate("/dashboard", { replace: true });
  };

  const handleLogout = async () => {
    const userRole = localStorage.getItem("user_role");
    try {
      await AuthService.logout(userRole);
    } catch (error) {
      console.warn("Erreur logout serveur :", error.message);
    } finally {
      localStorage.clear();
      setIsLoggedIn(false);
      navigate("/login", { replace: true });
    }
  };

  const PrivateRoute = ({ children }) => {
    if (isCheckingAuth) return null;
    return isLoggedIn ? children : <Navigate to="/login" replace />;
  };

  // ✅ RoleRoute — redirige vers /dashboard si rôle non autorisé
  const RoleRoute = ({ allowedRoles, children }) => {
    if (isCheckingAuth) return null;
    const role = localStorage.getItem("user_role");
    return allowedRoles.includes(role)
      ? children
      : <Navigate to="/dashboard" replace />;
  };

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Routes>
          {/* Route login */}
          <Route
            path="/login"
            element={
              isCheckingAuth ? (
                <div>Chargement...</div>
              ) : isLoggedIn ? (
                <Navigate to="/dashboard" replace />
              ) : isSignUp ? (
                <Register onToggle={() => setIsSignUp(false)} />
              ) : (
                <Login
                  onToggle={() => setIsSignUp(true)}
                  onLoginSuccess={handleLoginSuccess}
                />
              )
            }
          />

          <Route path="/login/reset-password" element={<ResetPassword />} />
          <Route path="/mot-de-passe-oublie" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<UpdatePassword />} />

          {/* Routes protégées */}
          <Route
            path="/*"
            element={
              <PrivateRoute>
                <Box sx={{ display: "flex", minHeight: "100vh" }}>
                  <Sidebar
                    isSidebar={isSidebar}
                    onLogout={handleLogout}
                    isCollapsed={isCollapsed}
                    setIsCollapsed={setIsCollapsed}
                  />
                  <Box
                    component="main"
                    sx={{
                      flexGrow: 1,
                      marginLeft: { xs: 0, md: isCollapsed ? "80px" : "280px" },
                      transition: "margin-left 0.3s ease",
                      width: {
                        xs: "100%",
                        md: isCollapsed ? "calc(100% - 80px)" : "calc(100% - 280px)",
                      },
                      minHeight: "100vh",
                      overflowX: "hidden",
                    }}
                  >
                    <Topbar setIsSidebar={setIsSidebar} onLogout={handleLogout} />
                    <Routes>

                      {/* ✅ Dashboard — admin ET gestionnaire */}
                      <Route
                        path="/dashboard"
                        element={
                          <RoleRoute allowedRoles={["admin", "gestionnaire"]}>
                            <Dashboard />
                          </RoleRoute>
                        }
                      />

                      {/* ✅ Utilisateurs — admin UNIQUEMENT */}
                      <Route
                        path="/utilisateurs"
                        element={
                          <RoleRoute allowedRoles={["admin"]}>
                            <Team />
                          </RoleRoute>
                        }
                      />

                      {/* Alertes — admin ET gestionnaire */}
                      <Route
                        path="/alerte"
                        element={
                          <RoleRoute allowedRoles={["admin", "gestionnaire"]}>
                            <Alerte />
                          </RoleRoute>
                        }
                      />

                      {/* Propriété intellectuelle — admin ET gestionnaire */}
                      <Route
                        path="/marques"
                        element={
                          <RoleRoute allowedRoles={["admin", "gestionnaire"]}>
                            <Contacts />
                          </RoleRoute>
                        }
                      />
                      <Route
                        path="/NomDomaine"
                        element={
                          <RoleRoute allowedRoles={["admin", "gestionnaire"]}>
                            <NomDomaine />
                          </RoleRoute>
                        }
                      />
                      <Route
                        path="/brevets"
                        element={
                          <RoleRoute allowedRoles={["admin", "gestionnaire"]}>
                            <Brevets />
                          </RoleRoute>
                        }
                      />
                      <Route
                        path="/contrats"
                        element={
                          <RoleRoute allowedRoles={["admin", "gestionnaire"]}>
                            <Contrat />
                          </RoleRoute>
                        }
                      />

                      {/* ✅ Espace personnel — admin ET gestionnaire */}
                      <Route
                        path="/EditProfil"
                        element={
                          <RoleRoute allowedRoles={["admin", "gestionnaire"]}>
                            <EditProfil />
                          </RoleRoute>
                        }
                      />
                      <Route
                        path="/Calendrier"
                        element={
                          <RoleRoute allowedRoles={["admin", "gestionnaire"]}>
                            <Calendrier />
                          </RoleRoute>
                        }
                      />
                      <Route
                        path="/contact"
                        element={
                          <RoleRoute allowedRoles={["admin", "gestionnaire"]}>
                            <Contact />
                          </RoleRoute>
                        }
                      />

                      {/* Autres routes */}
                      <Route path="/form" element={<Form />} />
                      <Route path="/bar" element={<Bar />} />
                      <Route path="/pie" element={<Pie />} />
                      <Route path="/line" element={<Line />} />
                      <Route path="/geography" element={<Geography />} />

                      {/* ✅ Redirection par défaut vers /dashboard */}
                      <Route path="*" element={<Navigate to="/dashboard" replace />} />

                    </Routes>
                  </Box>
                </Box>
              </PrivateRoute>
            }
          />
        </Routes>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          TransitionComponent={Slide}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            sx={{ borderRadius: "10px", boxShadow: 3 }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default App;